import { withOverviewThumbs } from "../overviewThumbs/overviewThumbRegistry";

function orgHas(gates, key) {
  return gates.hasOrgService?.(key) ?? true;
}

function hasGuestOpsEntitlement(gates) {
  return (
    orgHas(gates, "skin_analysis") ||
    orgHas(gates, "hair_analysis") ||
    orgHas(gates, "customer_engagement") ||
    orgHas(gates, "hair_style") ||
    orgHas(gates, "hair_coloring")
  );
}

function hasFlowAnalyticsEntitlement(gates) {
  return orgHas(gates, "hair_style") || orgHas(gates, "hair_coloring");
}

/**
 * @param {'workspace'|'partner'|'executive'} portal
 * @param {{ canViewStoreKpis: () => boolean, canManageSkinSettings: () => boolean, canManageServices: () => boolean, canManageProducts: () => boolean, isFranchise: () => boolean, hasOrgService?: (key: string) => boolean }} gates
 */
export function getAnalyticsOverviewSections(portal, gates) {
  const base =
    portal === "workspace"
      ? "/naturals/workspace"
      : portal === "executive"
        ? "/naturals/executive"
        : "/naturals/partner";

  if (portal === "executive") {
    const cards = withOverviewThumbs([
      {
        id: "exec-dashboard",
        title: "Executive dashboard",
        description: "Franchise map, network KPIs, and HQ oversight.",
        path: `${base}/super-admin-dashboard`,
      },
      {
        id: "network-kpis",
        title: "Salon KPIs",
        description: "Walk-ins, conversions, and sales by franchise.",
        path: `${base}/naturals-kpis`,
      },
      {
        id: "check-in-analytics",
        title: "Check-in analytics",
        description: "Queue volume, wait times, and stylist leaderboard.",
        path: `${base}/check-in-analytics`,
      },
      {
        id: "customer-analytics",
        title: "Customer analytics",
        description: "Guest segments and activity across the network.",
        path: `${base}/customer-analytics`,
      },
      {
        id: "skin-funnel",
        title: "Skin · funnel",
        description: "Skin analysis sessions and recommendation funnel.",
        path: `${base}/analytics/skin/funnel`,
      },
      {
        id: "hair-funnel",
        title: "Hair · funnel",
        description: "Hair analysis flow and completions.",
        path: `${base}/analytics/hair/funnel`,
      },
      {
        id: "customers",
        title: "Customer directory",
        description: "Search guests and open profiles.",
        path: `${base}/customers`,
      },
      {
        id: "hq-admins",
        title: "HQ admin access",
        description: "Workspace admins and permissions.",
        path: `${base}/hq-admins`,
      },
      {
        id: "regions",
        title: "Regions",
        description: "Regional structure and scoped access.",
        path: `${base}/regions`,
      },
      {
        id: "licenses",
        title: "Licenses",
        description: "Outlet licenses and activation.",
        path: `${base}/licenses`,
      },
      {
        id: "complaints",
        title: "Complaints",
        description: "Guest complaints across franchises.",
        path: `${base}/complaints`,
      },
      {
        id: "company-profile",
        title: "Company profile",
        description: "Branding and organization settings.",
        path: `${base}/company-profile`,
      },
    ]);
    return [
      {
        id: "performance",
        title: "Executive overview",
        cards: cards.filter((c) =>
          ["exec-dashboard", "network-kpis", "company-profile"].includes(c.id),
        ),
      },
      {
        id: "operations",
        title: "Operations & analytics",
        cards: cards.filter((c) => {
          const opsIds = [
            "check-in-analytics",
            "customer-analytics",
            "customers",
          ];
          if (orgHas(gates, "skin_analysis")) opsIds.push("skin-funnel");
          if (orgHas(gates, "hair_analysis")) opsIds.push("hair-funnel");
          return opsIds.includes(c.id);
        }),
      },
      {
        id: "administration",
        title: "Administration",
        cards: cards.filter((c) =>
          ["hq-admins", "regions", "licenses", "complaints"].includes(c.id),
        ),
      },
    ].filter((s) => s.cards.length);
  }
  const isHq = portal === "workspace" && !gates.isFranchise?.();
  const canKpi = gates.canViewStoreKpis?.() ?? false;
  const canSkin =
    (gates.canManageSkinSettings?.() ?? true) && orgHas(gates, "skin_analysis");
  const canServices =
    (gates.canManageServices?.() ?? false) &&
    (orgHas(gates, "skin_analysis") || orgHas(gates, "hair_analysis"));
  const canProducts =
    (gates.canManageProducts?.() ?? false) &&
    (orgHas(gates, "skin_analysis") || orgHas(gates, "hair_analysis"));

  const sections = [];

  if (canKpi) {
    const network = [];
    if (isHq) {
      network.push({
        id: "network-dashboard",
        title: "Network dashboard",
        description: "Franchise map, executive brief, and network-wide KPI snapshot.",
        path: `${base}/admin-dashboard`,
      });
      network.push({
        id: "network-kpis",
        title: "Franchise performance KPIs",
        description: "Walk-ins, conversions, sales, and per-franchise skin analysis counts.",
        path: `${base}/naturals-kpis`,
      });
    } else {
      network.push({
        id: "salon-dashboard",
        title: portal === "workspace" ? "Franchise dashboard" : "Salon dashboard",
        description:
          portal === "workspace"
            ? "Regional or franchise-scoped KPIs and network tools."
            : "Day-to-day salon ops, services, products, and skin insights.",
        path: portal === "workspace" ? `${base}/admin-dashboard` : `${base}/ib-dashboard`,
      });
      network.push({
        id: "salon-kpis",
        title: "Salon KPIs",
        description: "Walk-ins, queue, conversions, and sales for your store.",
        path: `${base}/naturals-kpis`,
      });
    }
    sections.push({ id: "performance", title: isHq ? "Network performance" : "Salon performance", cards: network });
  }

  const skinCards = [];
  if (canKpi && orgHas(gates, "skin_analysis")) {
    skinCards.push({
      id: "skin-funnel",
      title: "Skin · funnel & recommendations",
      description: "Analysis sessions, completions, top recommended services and products.",
      path: `${base}/analytics/skin/funnel`,
    });
    skinCards.push({
      id: "skin-services",
      title: "Skin · service KPIs",
      description: "Service catalog usage, taken lines, and staff attribution (network or store).",
      path: `${base}/analytics/skin/services`,
    });
    if (isHq) {
      skinCards.push({
        id: "skin-network-kpis",
        title: "Skin · network KPIs",
        description: "Skin analysis counts and service/product conversion across franchises.",
        path: `${base}/naturals-kpis`,
      });
    }
  }
  if (skinCards.length) {
    sections.push({ id: "skin", title: "Skin analysis", cards: skinCards });
  }

  const hairCards = [];
  if (canKpi && orgHas(gates, "hair_analysis")) {
    hairCards.push({
      id: "hair-funnel",
      title: "Hair · funnel & recommendations",
      description: "Hair analysis flow, completions, and top recommended services and products.",
      path: `${base}/analytics/hair/funnel`,
    });
    hairCards.push({
      id: "hair-services",
      title: "Hair · service KPIs",
      description: "Taken services and catalog performance tied to hair journeys.",
      path: `${base}/analytics/hair/services`,
    });
  }
  if (hairCards.length) {
    sections.push({ id: "hair", title: "Hair analysis", cards: hairCards });
  }

  const ops = [];
  if (canKpi && hasGuestOpsEntitlement(gates)) {
    if (portal === "partner") {
      ops.push({
        id: "check-ins",
        title: "Live floor",
        description: "Today’s check-in queue and in-service guests.",
        path: `${base}/check-ins`,
      });
      ops.push({
        id: "engagement-appointments",
        title: "Appointments",
        description: "Today’s engagement bookings and desk scheduling (same as live floor check-in).",
        path: `${base}/operations/appointments`,
      });
      ops.push({
        id: "visit-services",
        title: "Visit services",
        description: "Services linked to visits and desk workflow.",
        path: `${base}/visit-services`,
      });
      ops.push({
        id: "customers",
        title: "Customers",
        description: "Guest list and profiles for your franchise.",
        path: `${base}/customers`,
      });
    }
    ops.push({
      id: "check-in-analytics",
      title: "Check-in analytics",
      description: "Queue volume, wait times, stylist leaderboard, and franchise breakdown.",
      path: `${base}/check-in-analytics`,
    });
    ops.push({
      id: "customer-analytics",
      title: "Customer analytics",
      description: "Guest segments, repeat visits, and spend patterns.",
      path: `${base}/customer-analytics`,
    });
    if (portal === "partner" && hasFlowAnalyticsEntitlement(gates)) {
      ops.push({
        id: "flow-all",
        title: "All flow analytics",
        description: "Hairstyle, coloring, and combined flow access.",
        path: `${base}/flow-analytics`,
      });
    }
  }
  if (ops.length) {
    sections.push({ id: "operations", title: "Operations & guests", cards: ops });
  }

  if (
    (portal === "partner" || portal === "workspace") &&
    orgHas(gates, "customer_engagement")
  ) {
    const isHqEng = portal === "workspace";
    sections.push({
      id: "engagement",
      title: "Customer engagement",
      cards: [
        {
          id: "engagement",
          title: isHqEng ? "Engagement HQ" : "Engagement desk",
          description: isHqEng
            ? "Automations, Instagram, conversations, leads, and voice — network-wide HQ control."
            : "Appointments, leads, conversations, and voice — your franchise customer engagement hub.",
          path: `${base}/engagement`,
        },
      ],
    });
  }

  if (portal === "workspace" && orgHas(gates, "whatsapp")) {
    sections.push({
      id: "whatsapp",
      title: "WhatsApp",
      cards: [
        {
          id: "whatsapp",
          title: "WhatsApp",
          description:
            "Connect the WhatsApp account, attach a number to each branch, and turn the skin and hair report and the feedback message on or off.",
          path: `${base}/whatsapp`,
        },
      ],
    });
  }

  if (portal === "partner" && orgHas(gates, "reels")) {
    sections.push({
      id: "reels",
      title: "Reels & content",
      cards: [
        {
          id: "reels",
          title: "Transformation reels",
          description: "View, download, and manage before/after reels captured at your store.",
          path: `${base}/reels`,
        },
      ],
    });
  }

  if (portal === "partner") {
    sections.push({
      id: "staff",
      title: "Staff & team",
      cards: [
        {
          id: "staff",
          title: "Team roster",
          description: "Add stylists and roles — shared with customer engagement bookings.",
          path: `${base}/staff`,
        },
        {
          id: "staff-availability",
          title: "Schedule & leave",
          description: "Booking slots and mark time off (customer calendar only).",
          path: `${base}/staff-availability`,
        },
        {
          id: "staff-report",
          title: "Staff report & CSV",
          description: "Monthly performance per stylist — check-ins, services, flows.",
          path: `${base}/staff-report`,
        },
      ],
    });
  }

  if (portal === "partner") {
    const storeCards = [];
    if (orgHas(gates, "skin_analysis") || orgHas(gates, "hair_analysis")) {
      storeCards.push(
        {
          id: "price-list",
          title: "Service price list",
          description: "Franchise service catalog and pricing.",
          path: `${base}/price-list`,
        },
        {
          id: "product-list",
          title: "Product list",
          description: "Products available for recommendations and retail.",
          path: `${base}/product-list`,
        },
      );
    }
    if (
      orgHas(gates, "reels") ||
      orgHas(gates, "skin_analysis") ||
      orgHas(gates, "hair_analysis")
    ) {
      storeCards.push({
        id: "apps",
        title: "Store apps",
        description: "Salon, passport, and reels app downloads.",
        path: `${base}/apps`,
      });
    }
    if (orgHas(gates, "skin_analysis")) {
      storeCards.push({
        id: "kiosk-login",
        title: "Device login",
        description: "Kiosk credentials and device passwords.",
        path: `${base}/kiosk-login`,
      });
    }
    if (gates.canManageDashboardStaff?.()) {
      storeCards.push({
        id: "dashboard-staff",
        title: "Dashboard staff logins",
        description:
          "Create view-only accounts for operations, guests, team roster, and reels (same Naturals login).",
        path: `${base}/dashboard-staff`,
      });
    }
    if (storeCards.length) {
      sections.push({ id: "store", title: "Store & apps", cards: storeCards });
    }
  }

  const isOrgScope = gates.isOrganizationScope?.() ?? isHq;

  if (portal === "workspace" && isHq) {
    const admin = [];
    if (gates.canCreateFranchiseUsers?.()) {
      admin.push({
        id: "franchise-users",
        title: "Franchise users",
        description: "Create and manage franchise salon logins.",
        path: `${base}/franchise-users`,
      });
      if (isOrgScope) {
        admin.push(
          {
            id: "regions",
            title: "Regions",
            description: "Regional structure and scoped admins.",
            path: `${base}/regions`,
          },
          {
            id: "licenses",
            title: "Licenses",
            description: "Outlet licenses and activation.",
            path: `${base}/licenses`,
          },
        );
      }
    }
    if (gates.canViewComplaints?.()) {
      admin.push({
        id: "complaints",
        title: "Complaints",
        description: "Guest complaints across the network.",
        path: `${base}/complaints`,
      });
    }
    if (gates.canViewContactMessages?.()) {
      admin.push({
        id: "contact-inbox",
        title: "Contact inbox",
        description: "Messages from franchise contact forms.",
        path: `${base}/admin-contact-messages`,
      });
    }
    if (gates.canViewStoreKpis?.()) {
      admin.push({
        id: "location-requests",
        title: "Location requests",
        description: "Pending store location change requests.",
        path: `${base}/store-location-requests`,
      });
    }
    if (gates.isAdmin?.()) {
      admin.push({
        id: "geofence-settings",
        title: "Kiosk geofence",
        description: "Location lock for kiosk devices across the network.",
        path: `${base}/geofence-settings`,
      });
    }
    if (admin.length) {
      sections.push({ id: "administration", title: "Administration", cards: admin });
    }
  }

  if (
    portal === "workspace" &&
    isHq &&
    orgHas(gates, "skin_analysis") &&
    (gates.canEditPageSetup?.() || gates.canManageKiosk?.())
  ) {
    const kiosk = [];
    if (gates.canManageKiosk?.()) {
      kiosk.push({
        id: "kiosk-register-forms",
        title: "Registration form",
        description: "Kiosk guest registration forms.",
        path: `${base}/kiosk-register-forms`,
      });
    }
    if (gates.canEditPageSetup?.()) {
      kiosk.push({
        id: "page-setup",
        title: "Theme & page setup",
        description: "Kiosk theme, flows, and branded pages.",
        path: `${base}/page-setup`,
      });
      kiosk.push({
        id: "franchise-themes",
        title: "Saved themes",
        description: "Reusable theme packs for franchises.",
        path: `${base}/franchise-themes`,
      });
    }
    if (kiosk.length) {
      sections.push({ id: "kiosk", title: "Kiosk & branding", cards: kiosk });
    }
  }

  if (portal === "workspace" && (canSkin || canServices || canProducts)) {
    const setupCards = [];
    if (canSkin) {
      setupCards.push({
        id: "skin-settings",
        title: "Skin analysis settings",
        description: "Kiosk flow, AI agents, and analysis configuration.",
        path: `${base}/skin-analysis-settings`,
      });
    }
    if (canServices) {
      setupCards.push({
        id: "service-list-mapped",
        title: "Service list mapped",
        description: "Map HQ named service lists to franchises and pricing.",
        path: `${base}/servicelist`,
      });
      setupCards.push({
        id: "service-list-management",
        title: "Service list management",
        description: "Create, edit, and organize services in your HQ catalog.",
        path: `${base}/service-management`,
      });
    }
    if (canProducts) {
      setupCards.push({
        id: "product-list",
        title: "Product catalog setup",
        description: "Tagged products used in skin and hair recommendations.",
        path: `${base}/product-list`,
      });
    }
    if (setupCards.length) {
      sections.push({ id: "setup", title: "Analysis & catalog setup", cards: setupCards });
    }
  }

  let result = sections.filter((s) => s.cards?.length);
  if (gates.isDashboardStaff?.()) {
    const allowed = new Set(["operations", "staff", "reels"]);
    result = result.filter((s) => allowed.has(s.id));
  }
  return result.map((section) => ({
    ...section,
    cards: withOverviewThumbs(section.cards),
  }));
}
